import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCarouselProps<T> {
  carouselData: T[];
  isVisible: boolean;
  transitionDuration?: number; // Optional custom transition duration
  autoAdvanceInterval?: number; // Optional custom auto-advance interval
}

/**
 * Hook to manage animated carousel logic including auto-play timer and manual navigation
 * @template T The type of data items in the carousel
 * @param carouselData Array of data
 * @param isVisible Whether the component is visible (controls auto-play)
 * @returns Object with current state and handlers
 *
 * @example
 * ```typescript
 * const MyCarousel = useTestimonialLogic<MyType>({
 *   carouselData: MyTypeArray,
 *   isVisible: true
 * });
 * ```
 */
export const useCarousel = <T>({
  carouselData,
  isVisible,
  transitionDuration = 200, // Default to 200ms
  autoAdvanceInterval = 5000, // Default to 5 seconds
}: UseCarouselProps<T>) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to clear the timer
  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Helper function to advance to next testimonial with transition
  const advanceToNext = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % carouselData.length);
      setIsTransitioning(false);
    }, transitionDuration);
  }, [carouselData.length, transitionDuration]);

  // Helper function to start the auto-advance timer
  const startTimer = useCallback(() => {
    if (isVisible) {
      intervalRef.current = setInterval(advanceToNext, autoAdvanceInterval);
    }
  }, [isVisible, advanceToNext, autoAdvanceInterval]);

  // Auto-play timer effect
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    startTimer();
    return clearTimer;
  }, [isVisible, startTimer, clearTimer]);

  // Reset animation when visibility changes
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [isVisible]);

  // TODO: extract this to be a part of a Carousel component when we need it. It works for now at reducing the noise of Testimonials.tsx
  const handleIndicatorClick = (indicatorIndex: number) => {
    // Only reset timer if user navigates to a different testimonial
    if (indicatorIndex === currentIndex) {
      return;
    }
    clearTimer();

    setIsTransitioning(true);
    setAnimationKey(prev => prev + 1); // Restart progress bar animation

    setTimeout(() => {
      setCurrentIndex(indicatorIndex);
      setIsTransitioning(false);
    }, transitionDuration);

    startTimer(); // Restart timer

    // If clicking current indicator, do nothing (no timer reset)
  };

  const currentItem = carouselData[currentIndex];

  return {
    currentIndex,
    currentItem,
    isTransitioning,
    animationKey,
    handleIndicatorClick,
  };
};
